import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as createLogger, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$7 } from './cl-animation2.js';
import { d as defineCustomElement$6 } from './cl-field2.js';
import { d as defineCustomElement$5 } from './cl-icon2.js';
import { d as defineCustomElement$4 } from './cl-icon-button2.js';
import { d as defineCustomElement$3 } from './cl-tooltip2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-select-field';

const ClSelectField$1 = /*@__PURE__*/ proxyCustomElement(class ClSelectField extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clChange = createEvent(this, "clChange", 7);
    this.change = createEvent(this, "change", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Whether the select-field component has been initialized or not
     */
    this.hasInitialized = false;
    /**
     * Handles emitting the latest value of the select-field
     */
    this.handleDropdownChange = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.updateValue(event.detail.value);
    };
    /**
     * Handles toggling the cl-field's active state by clicking on the cl-field element
     */
    this.handleDropdownCloseStart = () => {
      if (this.fieldElement) {
        this.fieldElement.click();
      }
    };
    /**
     * Handles toggling the open state of the dropdown-element
     */
    this.handleToggleDropdown = (event) => {
      const shouldOpen = event.type === 'clShowContent';
      this.openDropdown = shouldOpen;
      if (this.slottedDropdownElement) {
        this.slottedDropdownElement.setAttribute('open', `${shouldOpen}`);
      }
    };
    /**
     * Updates the value of the select-field, based on whether the select-field is controlled or not
     */
    this.updateValue = (value) => {
      if (this.uncontrolled) {
        this.value = value;
      }
      if (this.hasInitialized) {
        emitEvent(this, 'clChange', { component, value }, value);
      }
    };
    this.openDropdown = false;
    this.closeOnSelect = false;
    this.disabled = false;
    this.floatingLabel = false;
    this.fullWidth = false;
    this.helperText = '';
    this.label = '';
    this.multiline = false;
    this.placeholder = '';
    this.showDetails = false;
    this.uncontrolled = false;
    this.value = [];
    this.variant = 'default';
  }
  /**
   * Sets the closeOnSelect attribute of the dropdown element to true or false based on the closeOnSelect attribute
   */
  handleCloseOnSelect(closeOnSelect) {
    if (this.slottedDropdownElement) {
      this.slottedDropdownElement.setAttribute('close-on-select', `${closeOnSelect}`);
      if (closeOnSelect) {
        this.slottedDropdownElement.addEventListener('clCloseStart', this.handleDropdownCloseStart);
      }
      else {
        this.slottedDropdownElement.removeEventListener('clCloseStart', this.handleDropdownCloseStart);
      }
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Set the slotted dropdown-element;
   * - Set the value of the slotted dropdown-element;
   * - Set the full-width attribute of the slotted dropdown-element to true;
   * - Add eventListener for the `change` event to the slotted element;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'change', 'clChange');
    const slottedDropdownElement = this.element.querySelector('cl-dropdown') ||
      this.element.querySelector('span > cl-dropdown');
    if (slottedDropdownElement) {
      this.slottedDropdownElement = slottedDropdownElement;
      slottedDropdownElement.setAttribute('full-width', 'true');
      slottedDropdownElement.addEventListener('clChange', this.handleDropdownChange, { capture: true });
      this.handleCloseOnSelect(this.closeOnSelect);
    }
  }
  /**
   * Every time the component is disconnected from the DOM:
   * - Remove the `change` event listener;
   * - Remove the `closeStart` event listener;
   */
  disconnectedCallback() {
    if (this.slottedDropdownElement) {
      this.slottedDropdownElement.removeEventListener('clChange', this.handleDropdownChange);
      this.slottedDropdownElement.removeEventListener('clCloseStart', this.handleDropdownCloseStart);
    }
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Set the initial value of the select-field (if one is pre-selected in the slotted dropdown);
   * - Set `hasInitialized` to true to indicate that the component has been initialized
   */
  componentDidLoad() {
    var _a, _b;
    const initialValue = (_b = (_a = this.slottedDropdownElement) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : [];
    if (initialValue.length) {
      this.updateValue(initialValue);
    }
    this.hasInitialized = true;
  }
  render() {
    const { disabled, floatingLabel, fullWidth, helperText, label, multiline, placeholder, showDetails, value, variant, } = this;
    return (h("cl-field", { ref: el => {
        this.fieldElement = el;
      }, disabled: disabled, floatingLabel: floatingLabel, fullWidth: fullWidth, helperText: helperText, label: label, multiline: multiline, placeholder: placeholder, showDetails: showDetails, value: value.map(item => item.label).join(', '), variant: variant, onClHideContent: this.handleToggleDropdown, onClShowContent: this.handleToggleDropdown }, h("span", { slot: 'leading' }, h("slot", { name: 'leading' })), h("span", { slot: 'content' }, h("slot", null)), h("span", { slot: 'trailing' }, h("cl-animation", { duration: 75, rotate: this.openDropdown ? '[0,0,180]' : '[0,0,0]', play: true }, h("cl-icon-button", { "icon-name": 'micro--chevron--down', slot: 'item', variant: 'tertiary' })))));
  }
  get element() { return this; }
  static get watchers() { return {
    "closeOnSelect": ["handleCloseOnSelect"]
  }; }
}, [6, "cl-select-field", {
    "closeOnSelect": [4, "close-on-select"],
    "disabled": [4],
    "floatingLabel": [4, "floating-label"],
    "fullWidth": [4, "full-width"],
    "helperText": [1, "helper-text"],
    "label": [1],
    "multiline": [4],
    "placeholder": [1],
    "showDetails": [4, "show-details"],
    "uncontrolled": [516],
    "value": [1040],
    "variant": [1],
    "openDropdown": [32]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-select-field", "cl-animation", "cl-field", "cl-icon", "cl-icon-button", "cl-tooltip", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-select-field":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClSelectField$1);
      }
      break;
    case "cl-animation":
      if (!customElements.get(tagName)) {
        defineCustomElement$7();
      }
      break;
    case "cl-field":
      if (!customElements.get(tagName)) {
        defineCustomElement$6();
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "cl-icon-button":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "cl-tooltip":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClSelectField = ClSelectField$1;
const defineCustomElement = defineCustomElement$1;

export { ClSelectField, defineCustomElement };

//# sourceMappingURL=cl-select-field.js.map